import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordResetConfirmSchema } from '@utazzo/shared';
import { UzAlert, UzButton, UzForm, UzFormField, UzInput, cn } from '@utazzo/ui';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { useResetPassword } from '../use-auth';

import { AuthLayout } from './AuthLayout';

import type { z } from 'zod';

type FormValues = z.infer<typeof PasswordResetConfirmSchema>;

const STRENGTH_LABELS = ['Weak', 'Fair', 'Good', 'Strong', 'Excellent'];
const STRENGTH_COLORS = [
  'bg-destructive',
  'bg-amber-500',
  'bg-yellow-500',
  'bg-emerald-500',
  'bg-emerald-500',
];

const scorePassword = (value: string): number => {
  let score = 0;
  if (value.length >= 12) score += 1;
  if (/\d/u.test(value)) score += 1;
  if (/[^a-zA-Z0-9]/u.test(value)) score += 1;
  if (/[a-z]/u.test(value) && /[A-Z]/u.test(value)) score += 1;
  return score;
};

const PasswordStrength = ({ value, strengthLabel }: { value: string; strengthLabel: string }) => {
  const score = scorePassword(value);
  if (!value) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="grid grid-cols-4 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1 rounded-full transition-colors',
              i < score ? STRENGTH_COLORS[score] : 'bg-muted',
            )}
          />
        ))}
      </div>
      <div className="text-muted-foreground flex justify-between text-xs">
        <span>
          {strengthLabel}{' '}
          <strong className="text-foreground font-medium">
            {STRENGTH_LABELS[Math.min(score, STRENGTH_LABELS.length - 1)]}
          </strong>
        </span>
        <span>{value.length}/12+</span>
      </div>
    </div>
  );
};

export const ResetPasswordPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const reset = useResetPassword();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(PasswordResetConfirmSchema),
    defaultValues: { token, newPassword: '', confirmPassword: '' },
  });

  const newPassword = form.watch('newPassword');

  const onSubmit = form.handleSubmit((values) => reset.mutate(values));

  if (!token) {
    return (
      <AuthLayout
        title={t('auth.reset.title')}
        footer={
          <Link to="/forgot-password" className="text-primary font-medium hover:underline">
            {t('common.backToLogin')}
          </Link>
        }
      >
        <UzAlert tone="destructive" title={t('auth.reset.missingToken')} />
      </AuthLayout>
    );
  }

  const toggle = (label: string) => (
    <button
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      className="text-muted-foreground hover:text-foreground -m-1 p-1 transition-colors"
      aria-label={label}
      tabIndex={-1}
    >
      {showPassword ? (
        <EyeOff className="h-4 w-4" aria-hidden />
      ) : (
        <Eye className="h-4 w-4" aria-hidden />
      )}
    </button>
  );

  return (
    <AuthLayout
      title={t('auth.reset.title')}
      subtitle={t('auth.reset.subtitle')}
      footer={
        <Link to="/login" className="text-primary font-medium hover:underline">
          {t('common.backToLogin')}
        </Link>
      }
    >
      <UzForm form={form} onSubmit={onSubmit}>
        <UzFormField name="newPassword">
          {({ field, error }) => (
            <UzInput
              {...field}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              label={t('auth.reset.newPasswordLabel')}
              leftAdornment={<Lock className="h-4 w-4" aria-hidden />}
              rightAdornment={toggle(
                showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword'),
              )}
              error={error}
              required
              autoFocus
              disabled={reset.isPending}
            />
          )}
        </UzFormField>

        <PasswordStrength value={newPassword} strengthLabel={t('auth.reset.strength')} />

        <UzFormField name="confirmPassword">
          {({ field, error }) => (
            <UzInput
              {...field}
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              label={t('auth.reset.confirmPasswordLabel')}
              leftAdornment={<Lock className="h-4 w-4" aria-hidden />}
              error={error}
              required
              disabled={reset.isPending}
            />
          )}
        </UzFormField>

        <UzButton type="submit" fullWidth loading={reset.isPending} className="mt-2">
          {reset.isPending ? t('auth.reset.submitting') : t('auth.reset.submit')}
        </UzButton>
      </UzForm>
    </AuthLayout>
  );
};
